import * as React from 'react';
import { toast } from 'react-toastify';
import ScheduleTiltCraneJobsData from './ScheduleTiltCraneJobs';
import { useNavigate } from 'react-router-dom';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useRef } from 'react';
import type { GenericList, PublicHoliday, ShutdownPeriod } from './models/JobSharedInterfaces';
import type { ItemOrder } from './models/JobScheduleInterfaces';

type TiltCraneDay = {
    scheduleDate: string;
    scheduleDateFormatted: string;
    scheduleDateFormattedSmall: string;
    canEdit: boolean;
    isCurrentDay: boolean;
    jobs: TiltCraneJob[];
}

type TiltCraneJob = {
    id: number;
    jobId: number;
    jobQuoteItemId: number;
    companyId: number;
    scheduledDate: string;
    truckId: number;
    truck: string;
    truckIsCraneTilt: boolean;
    customerId: number;
    customer: string;
    siteAddress: string;
    description: string;
    manualItem: boolean;
    type: string;
    typeId: number;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemTypeId: number;     //fix this as could be null
    quantityType: string;
    onOffhireDate: string;
    onOffhireDateFormatted: string;
    accountTypeId: number;
    accountType: string;
    accountChecked: boolean;
    goAhead: boolean;
    callout: boolean;
    truckAccess: string;
    parking: string;
    siteClean: string;
    powerlines: string;
    weekdayAccess: string;
    weekendAccess: string;
    phoneCallRequired: string;
    tiltSlideClearance: string;
    plumbingElectricityDisconnected: string;
    extras: string;
    isEws: boolean;
    showRedNotes: boolean;
    showGreenNotes: boolean;
    scheduleNotes: string;
    runsheetNotes: string;
    status: string;
    statusId: number;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    craneTruckRequired: boolean;
    jobOrder: number;
    isPlaceholder: boolean;
    purchaseOrderNumber: string;
    rowVersionTimestamp: Uint8Array;
    canEdit: boolean;
    canDelete: boolean;
}

const ScheduleTiltCraneData = () => {
    var date = new Date();
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [loadingDay, setLoadingDay] = React.useState(true);
    const [canEdit, setCanEdit] = React.useState(false);
    const [monthOptions, setMonthOptions] = React.useState<GenericList[]>([]);
    const [yearOptions, setYearOptions] = React.useState<GenericList[]>([]);
    const [scheduleBranchOptions, setScheduleBranchOptions] = React.useState<GenericList[]>([]);
    const [month, setMonth] = React.useState(date.getMonth() + 1);
    const [year, setYear] = React.useState(date.getFullYear());
    const [scheduleBranchId, setScheduleBranchId] = React.useState(1);
    const [dayJobs, setDayJobs] = React.useState<TiltCraneDay[]>([]);
    const [scheduleTypes, setScheduleTypes] = React.useState<GenericList[]>([]);
    const [trucks, setTrucks] = React.useState<GenericList[]>([]);
    const [publicHolidays, setPublicHolidays] = React.useState<PublicHoliday[]>([]);
    const [shutdownPeriods, setShutdownPeriods] = React.useState<ShutdownPeriod[]> ([]);
    const [reasonOptions, setReasonOptions] = React.useState<GenericList[]> ([]);
    const [systemComanyId, setSystemCompanyId] = React.useState(2);
    const [scrollScreen, setScrollScreen] = React.useState(false);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (scrollScreen && scrollRef && scrollRef.current) {
            const rect = scrollRef.current.getBoundingClientRect();
            var header = document.getElementById('header');
            var headerHeight = 0;
            if (header) {
                headerHeight = header.offsetHeight;
            }

            const element = document.getElementById("screen-container");
            if (element) {
                var options: ScrollToOptions = {
                    behavior: "smooth",
                    left: 0,
                    top: rect.top - headerHeight - 50
                };
                element.scrollTo(options);
            }
            setScrollScreen(false);
        }
    }, [scrollScreen]);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("ViewTiltCraneSchedule");
        if (hasPermission) {
            getTiltCraneSchedule();
        } else {
            navigate("/accessdenied");
        }
    }

    const getTiltCraneSchedule = async () => {
        setLoadingDay(true);

        const apiUrl = 'GetTiltCraneSchedule?month=' + month + "&year=" + year + "&scheduleBranchId=" + scheduleBranchId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setMonthOptions(result.data.monthOptions);
            setYearOptions(result.data.yearOptions);
            setScheduleBranchOptions(result.data.scheduleBranchOptions);
            setScheduleBranchId(result.data.scheduleBranchId);
            setDayJobs(result.data.days);
            setSystemCompanyId(result.data.systemCompanyId);
            setScheduleTypes(result.data.scheduleTypes);
            setTrucks(result.data.trucks);
            setPublicHolidays(result.data.publicHolidays);
            setShutdownPeriods(result.data.shutdownPeriods);
            setReasonOptions(result.data.reasonOptions);
            setLoading(false);
            setLoadingDay(false);
            setCanEdit(result.data.canEdit);
            setScrollScreen(true);
        }
    }

    const getTiltCraneJobs = async (searchMonth: number, searchYear: number, scrollToDay: boolean) => {
        setLoadingDay(true);

        const apiUrl = 'GetTiltCraneJobs?month=' + searchMonth + "&year=" + searchYear + "&scheduleBranchId=" + scheduleBranchId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setDayJobs(result.data);              
            setLoadingDay(false);

            if (scrollToDay) {
                setScrollScreen(true);
            }
        }
    }

    const getTiltCraneDayJobs = async (scheduleDate: string) => {
        var days = [...dayJobs];
        var selectedDay = days.find(d => d.scheduleDate == scheduleDate);
        if (selectedDay) {
            const apiUrl = 'GetTiltCraneDayJobs?scheduleDate=' + scheduleDate + "&scheduleBranchId=" + scheduleBranchId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                if (selectedDay) {
                    selectedDay.jobs = result.data;
                    setDayJobs(days);
                }
            }
        }
    }

    const executeScroll = () => {
        var divToScrollTo = document.getElementById('currentday');
        var header = document.getElementById('header');
        var headerHeight = 0;
        if (header) {
            headerHeight = header.offsetHeight;
        }

        if (divToScrollTo) {
            var options: ScrollToOptions = {
                behavior: "smooth",
                left: 0,
                top: divToScrollTo.offsetTop - headerHeight - 50
            };
            window.scrollTo(options);
        }
    }

    const refresh = () => {
        getTiltCraneJobs(month, year, false);
    }

    const resetSearch = () => {
        var date = new Date();
        var currentMonth = (date.getMonth() + 1);
        var currentYear = date.getFullYear();
        setMonth(currentMonth);
        setYear(currentYear);

        getTiltCraneJobs(currentMonth, currentYear, true);
    }

    const handleBranchChange = (e: any) => {
        var value = e.target.value * 1;
        setScheduleBranchId(value);
    }

    const handleMonthChange = (e: any) => {
        var value = e.target.value * 1;
        setMonth(value);
    }

    const handleYearChange = (e: any) => {
        var value = e.target.value * 1;
        setYear(value);
    }

    const saveDayOrder = (scheduleDate: string, jobs: TiltCraneJob[]) => {
        var items: ItemOrder[] = [];
        for (var i = 0; i < jobs.length; i++) {
            var job = jobs[i];
            var item: ItemOrder = {
                scheduleId: job.id,
                jobOrder: job.jobOrder,
                rowVersionTimestamp: job.rowVersionTimestamp
            };
            items.push(item);
        }

        saveOrder(scheduleDate, items);
    }

    const saveOrder = async (scheduleDate: string, items: ItemOrder[]) => {
        var jobs = [...dayJobs];
        var day = jobs.find(j => j.scheduleDate == scheduleDate);

        var updateJobOrder = {
            scheduleDate: scheduleDate,
            scheduleBranchId: scheduleBranchId,
            items: items
        };

        var jsonItem = JSON.stringify(updateJobOrder);

        const apiUrl = "SaveTiltCraneDayOrder";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                if (day) {
                    day.jobs = result.data.jobs;
                    setDayJobs(jobs);
                }
                toast.success("Schedule Saved");
            }
        }
    }

    const updateJob = (scheduleDate: string, jobIndex: number, updatedJob: TiltCraneJob) => {
        var jobs = [...dayJobs];
        var day = jobs.find(j => j.scheduleDate == scheduleDate);
        var job = day?.jobs[jobIndex];
        job = updatedJob;
        setDayJobs(jobs);
    }

    const updateDay = (scheduleDate: string) => {
        //refreshes the current day only
        getTiltCraneDayJobs(scheduleDate);
    }
    
    const renderDetails = (
        <div>
            <div className={loadingDay ? "" : "hidden"}>
                <p><em>Loading...</em></p>
            </div>
            <div className={loadingDay ? "hidden" : "scheduleTiltCrane marginTop135"}>
                <div id="header" className="schedulenew__search--fixed">
                    <div className="schedulenew__search--fixed-width">
                        <div className="schedulenew__statusBar">
                            <div>
                                <label className="input-group--inline" htmlFor="month">
                                    <select className="select" id="month" name="month" value={month} onChange={(e) => handleMonthChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {monthOptions.map(month =>
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="year">
                                    <select className="select" id="year" name="year" value={year} onChange={(e) => handleYearChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {yearOptions.map(year =>
                                            <option key={year.id} value={year.id}>{year.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="branchFilterId">
                                    <span className="label">Branch</span>
                                    <select className="select" id="branchFilterId" name="branchFilterId" value={scheduleBranchId} onChange={(e) => handleBranchChange(e)}>
                                        {scheduleBranchOptions.map(branch =>
                                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                                        )};
                                    </select>
                                </label>
                                <button className="defaultbutton defaultbutton__medium margin10" type="button" onClick={refresh}>Refresh</button>
                                <button className="defaultbutton defaultbutton__small margin10" type="button" onClick={resetSearch}>Reset</button>
                            </div>
                        </div>
                        <div className="marginLeft10">
                            <span className="allocate__customerNoteText">* Items in dark red need Customer unlocked.</span>
                        </div>
                        <div className="marginLeft10">
                            <span className="allocate__customerReckonNoteText">* Items in blue need Customer linked to Reckon.</span>
                        </div>
                        <hr></hr>
                    </div>
                </div>

                {dayJobs.map((day, index) =>  
                    <div className="scheduleRow paddingFull10" key={index}>
                        <div id={day.isCurrentDay ? "currentday" : ""} ref={day.isCurrentDay ? scrollRef : undefined}></div>
                        <ScheduleTiltCraneJobsData key={index} systemCompanyId={systemComanyId} scheduleBranchId={scheduleBranchId} canEdit={canEdit} scheduleTypes={scheduleTypes} trucks={trucks} publicHolidays={publicHolidays} shutdownPeriods={shutdownPeriods} reasonOptions={reasonOptions} scheduleDate={day.scheduleDate} scheduleDateFormatted={day.scheduleDateFormatted} scheduleDateFormattedSmall={day.scheduleDateFormattedSmall} jobs={day.jobs} saveDayOrder={saveDayOrder} saveJob={updateJob} updateDay={updateDay } />
                    </div>
                )}
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );

}

export default ScheduleTiltCraneData;